<template>
  <div class="object-select-input clearfix">
    <div class="form-group row">
      <label :class="options.fullWidth ? 'col-sm-12' : 'col-sm-4 col-form-label'">
        {{ options.label }}
        <span v-if="options.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-8'">
        <select class="form-control" ref="dropdown"
        :name="options.name"
        @change="validate()">
          <template v-if="options.optgroup">
            <template v-for="object in options.objects">
              <optgroup :label="object.text">
                <option v-for="val in object.children"
                :key="val.uuid"
                :value="val.uuid"
                :checked="val.uuid === value"
                :title="val[options.titleName]">{{ val[options.objectName] }}</option>
              </optgroup>
            </template>
          </template>
          <template v-else-if="options.objects.length > 0">
            <option v-for="(object, i) in options.objects"
            :key="i"
            :value="object.uuid"
            :checked="object.uuid === value">{{ object[options.objectName] }}</option>
          </template>
        </select>
        <span :class="validationClass" v-show="validationMessage">
          {{ validationMessage }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
  import Api from '@/vuex/api'
  import Choices from 'choices.js'
  import {
    validationClass,
    validationIcon,
    validationMessage
  } from '@/util/helpers'

  export default {
    name: 'ObjectSelectInput',
    props: {
      value: {
        type: String
      },
      options: {
        type: Object,
        default () {
          return {
            label: 'Text',
            name: 'object_select',
            objectName: 'fullName',
            titleName: 'organizationName',
            value: '',
            objects: [],
            required: false
          }
        }
      }
    },
    data () {
      return {
        isSearching: false,
        ignoreSort: false,
        isDirty: false,
        isValid: false,
        choices: false,
        updating: false
      }
    },
    computed: {
      label () {
        return this.options.singleLabel
          ? this.options.singleLabel
          : this.options.label
      },
      validationClass,
      validationIcon,
      validationMessage,
      invalidMessage () {
        return this.options.label + ' is required'
      }
    },
    mounted () {
      let $vm = this
      if (this.options.ignoreSort) {
        this.ignoreSort = this.options.ignoreSort
      }
      /* eslint-disable no-new */
      this.choices = new Choices(this.$refs.dropdown, {
        callbackOnCreateTemplates: function (template) {
          let classNames = this.config.classNames
          return {
            item: (data) => {
              let object = $vm.options.objects[data.choiceId - 1]
              let label = data.label
              let closeButton = `
              <button class="${classNames.button}" data-button>Remove item</button>
              `
              if (object && object.icon) {
                label = `<span class="${object.icon} fa-fw"></span> ${label}`
              }
              if (data.title) {
                label = `${label} ${data.title}`
              }
              if (this.config.removeItemButton) {
                label = `${label} ${closeButton}`
              }
              return template(`
              <div class="${classNames.item} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable}"  data-item data-id="${data.id}" data-value="${data.value}" ${data.active ? 'aria-selected="true"' : ''} ${data.disabled ? 'aria-disabled="true"' : ''}>
                ${label}
              </div>
              `)
            },
            choice: (data) => {
              let object = $vm.options.objects[data.id - 1]
              let label = data.label
              if (object && object.icon) {
                label = `<span class="fa-fw ${object.icon}"></span> ${label}`
              }
              return template(`
              <div class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable}" data-select-text="${this.config.itemSelectText}" data-choice ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'} data-id="${data.id}" data-value="${data.value}" ${data.groupId > 0 ? 'role="treeitem"' : 'role="option"'}>
                ${label}
              </div>
              `)
            }
          }
        },
        placeholder: true,
        shouldSort: !this.ignoreSort,
        removeItemButton: true,
        itemSelectText: '',
        placeholderValue: 'Select a ' + this.label,
        searchPlaceholderValue: 'Select a ' + this.label,
        fuseOptions: { threshold: 0.6 },
        searchResultLimit: 6,
        searchFields: ['label'],
        callbackOnInit: function () {
          if (!$vm.value) {
            let arr = this.store.getItemsFilteredByActive()
            if (arr.length > 0) {
              let value = arr.pop().value
              this.removeItemsByValue(value)
            }
          } else {
            this.setValueByChoice($vm.value)
          }
        }
      })
      document.removeEventListener('click', this.choices._onClick)
      document.removeEventListener('keyup', this.choices._onKeyUp)
      document.removeEventListener('keydown', this.choices._onKeyDown)
      document.addEventListener('click', (e) => {
        if (e.target.className.includes('choices') && e.target.tagName !== 'BUTTON') {
          this.choices._onClick(e)
        }
      }, true)
      document.addEventListener('keydown', (e) => {
        if (document.activeElement.className.includes('choices')) {
          if (e.keyCode === 13) {
            this.choices._onKeyDown(e)
            this.isSearching = false
          } else if (e.target.className.includes('choices')) {
            this.isSearching = true
            this.choices._onKeyDown(e)
          }
        } else {
          this.isSearching = false
        }
      }, true)
      document.addEventListener('keyup', (e) => {
        if (this.isSearching && document.activeElement.className.includes('choices')) {
          this.choices._onKeyUp(e)
        }
      }, true)
    },
    watch: {
      'options.objects': function (oldVal, newVal) {
        this.updateChoices()
      }
    },
    methods: {
      async updateChoices () {
        if (this.choices) {
          // updates the choices input with new values
          this.choices.clearInput()
          this.choices.config.searchPlaceholderValue = 'Select a ' + this.options.label
          this.choices.config.placeholderValue = 'Select a ' + this.options.label
          if (this.choices.containerInner.children[1].children[0]) {
            this.choices.containerInner.children[1].children[0].innerHTML = ''
          }
          this.choices.containerOuter.children[1].children[0].placeholder = 'Select a ' + this.options.label
          this.choices.setChoices(this.options.objects, 'uuid', this.options.objectName, true)
          if (this.value) {
            this.choices.setValueByChoice(this.value)
          }
        }
      },
      async validate (val) {
        await this.$nextTick()
        let data = false
        if (!val) {
          data = this.choices.getValue()
          val = data ? data.value : ''
        }
        this.$emit('input', val)
        if (this.options.required) {
          this.isDirty = true
          this.isValid = val && val.length > 0
        } else {
          this.isValid = true
        }
      },
      runWatcher (val, name) {
        switch (name) {
          case 'AccountUuid':
            this.getCars(val)
            break
          case 'allCars':
            if (val) {
              this.$el.style.display = 'none'
            } else {
              this.$el.style.display = 'block'
            }
            break
          case 'cars':
            if (val.length) {
              this.$el.style.display = 'none'
            } else {
              this.$el.style.display = 'block'
            }
            break
          case 'OrganizationUuid':
            // Only those Account Promo codes should be visible which are being created under selected organisation.
            this.getAccountPromos(val)
            break
        }
      },
      async getCars (val) {
        if (val) {
          let instance = new Api()
          let results = await instance.getUserCars({
            accountUuid: val
          })
          results.map(function (val, i) {
            results[i] = { value: val.uuid, label: val.dropdownLabel }
          })
          if (this.choices) {
            if (this.choices.getValue()) {
              let found = results.find(el => el.value === this.choices.getValue().value)
              if (!found) {
                this.choices.clearInput()
                if (this.choices.containerInner.children[1].children[0]) {
                  this.choices.containerInner.children[1].children[0].innerHTML = ''
                }
              }
            }
            this.choices.setChoices(results, 'value', 'label', true)
          }
        }
      },
      async getAccountPromos (val) {
        if (val) {
          let instance = new Api()
          let results = await instance.getOrgPromos({
            organizationUuid: val
          })
          results.map(function (val, i) {
            results[i] = { value: val.uuid, label: val.code }
          })
          if (this.choices.getValue()) {
            let found = results.find(el => el.value === this.choices.getValue().value)
            if (!found) {
              this.choices.clearInput()
              if (this.choices.containerInner.children[1].children[0]) {
                this.choices.containerInner.children[1].children[0].innerHTML = ''
              }
            }
          }
          this.choices.setChoices(results, 'value', 'label', true)
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/inputs/ObjectSelectInput.vue